import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/pavimento.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const PavimentoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_pavimento: '',
    id_escola: '',
    id_projeto: '',
    descr_pavimento: '',
    pe_direito: '',
    area: '',
    perimetro: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [escolaSearch, setEscolaSearch] = useState('');
  const [escolas, setEscolas] = useState([]);
  const [camposComErro, setCamposComErro] = useState([]);
  const [proximoId, setProximoId] = useState('');
  const [projetoSearch, setProjetoSearch] = useState('');
  const [projetos, setProjetos] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar escolas com base na busca
  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch || escolaSearch.trim() === '') return escolas;
    const searchTerm = escolaSearch.toLowerCase().trim();
    return escolas.filter(escola =>
      escola.nome_escola?.toLowerCase().includes(searchTerm) ||
      escola.id_escola?.toString().toLowerCase().includes(searchTerm) ||
      escola.sigla?.toLowerCase().includes(searchTerm)
    );
  }, [escolas, escolaSearch]);

  // Filtrar projetos com base na busca
  const projetosFiltrados = useMemo(() => {
    if (!projetoSearch || projetoSearch.trim() === '') return projetos;
    const searchTerm = projetoSearch.toLowerCase().trim();
    return projetos.filter(projeto =>
      projeto.id_projeto?.toLowerCase().includes(searchTerm) ||
      projeto.nome_escola?.toLowerCase().includes(searchTerm) ||
      projeto.descr_tp_projeto?.toLowerCase().includes(searchTerm)
    );
  }, [projetos, projetoSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchEscolas = useCallback(async () => {
    try {
      const response = await api.get('/escola');
      setEscolas(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar escolas: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchProjetos = useCallback(async () => {
    try {
      const response = await api.get('/projeto');
      setProjetos(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar projetos: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/pavimento');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/pavimento');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  // ID é gerado automaticamente no backend ao clicar em Adicionar
  const fetchNextId = useCallback(() => {
    // Não busca mais o próximo ID - será gerado atomicamente no backend
  }, []);

  useEffect(() => {
    verificarAPI();
    fetchEscolas();
    fetchProjetos();
    carregarDados();
    fetchNextId();
  }, [verificarAPI, fetchEscolas, fetchProjetos, carregarDados, fetchNextId]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    try {
      const response = await api.put(`/pavimento/${id}`, dadosAtualizacao);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    try {
      await api.delete(`/pavimento/${id}`);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
      carregarDados(false);
    } catch (error) {
      mostrarMensagem('error', error.message);
    }
  }, [mostrarMensagem, carregarDados]);

  const colunas = useMemo(() => {
    const escolasNomes = escolas.reduce((acc, item) => ({ ...acc, [item.id_escola]: item.nome_escola }), {});
    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_pavimento, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_pavimento}"?`)) {
            excluirRegistro(data.id_pavimento);
          }
        },
        headerSort: false
      },
      {
        title: 'ID Pavimento',
        field: 'id_pavimento',
        width: 170,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Escola',
        field: 'id_escola',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar escola...',
        sorter: 'string',
        formatter: (cell) => escolasNomes[cell.getValue()] || cell.getValue(),
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeEscola = escolasNomes[rowValue] || rowValue || '';
          return nomeEscola.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: { values: escolasNomes }
      },
      {
        title: 'ID Projeto',
        field: 'id_projeto',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Descrição',
        field: 'descr_pavimento',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Pé Direito',
        field: 'pe_direito',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar pé direito...',
        sorter: 'number',
        formatter: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined || value === '') return '';
          return parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = parseFloat(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: function (cell, onRendered, success, cancel) {
          const input = document.createElement('input');
          input.type = 'text';
          input.style.width = '100%';
          input.style.padding = '4px';
          input.style.boxSizing = 'border-box';

          const currentValue = cell.getValue();
          if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
            input.value = parseFloat(currentValue).toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }

          input.addEventListener('input', (e) => {
            let value = e.target.value.replace(/\D/g, '');
            if (value) {
              value = (parseInt(value) / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              e.target.value = value;
            }
          });

          onRendered(() => {
            input.focus();
            input.select();
          });

          const successFunc = () => {
            const rawValue = input.value.replace(/\./g, '').replace(',', '.');
            success(parseFloat(rawValue) || 0);
          };

          input.addEventListener('blur', successFunc);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              successFunc();
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Área',
        field: 'area',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar área...',
        sorter: 'number',
        formatter: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined || value === '') return '';
          return parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = parseFloat(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: function (cell, onRendered, success, cancel) {
          const input = document.createElement('input');
          input.type = 'text';
          input.style.width = '100%';
          input.style.padding = '4px';
          input.style.boxSizing = 'border-box';

          const currentValue = cell.getValue();
          if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
            input.value = parseFloat(currentValue).toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }

          input.addEventListener('input', (e) => {
            let value = e.target.value.replace(/\D/g, '');
            if (value) {
              value = (parseInt(value) / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              e.target.value = value;
            }
          });

          onRendered(() => {
            input.focus();
            input.select();
          });

          const successFunc = () => {
            const rawValue = input.value.replace(/\./g, '').replace(',', '.');
            success(parseFloat(rawValue) || 0);
          };

          input.addEventListener('blur', successFunc);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              successFunc();
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Perímetro',
        field: 'perimetro',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar perímetro...',
        sorter: 'number',
        formatter: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined || value === '') return '';
          return parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = parseFloat(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: function (cell, onRendered, success, cancel) {
          const input = document.createElement('input');
          input.type = 'text';
          input.style.width = '100%';
          input.style.padding = '4px';
          input.style.boxSizing = 'border-box';

          const currentValue = cell.getValue();
          if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
            input.value = parseFloat(currentValue).toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }

          input.addEventListener('input', (e) => {
            let value = e.target.value.replace(/\D/g, '');
            if (value) {
              value = (parseInt(value) / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              e.target.value = value;
            }
          });

          onRendered(() => {
            input.focus();
            input.select();
          });

          const successFunc = () => {
            const rawValue = input.value.replace(/\./g, '').replace(',', '.');
            success(parseFloat(rawValue) || 0);
          };

          input.addEventListener('blur', successFunc);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              successFunc();
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      }
    ]
  }, [escolas, atualizarRegistro, excluirRegistro]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_pavimento'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(colunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, colunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_escola, id_projeto, descr_pavimento, pe_direito, area, perimetro } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_escola) {
      camposObrigatorios.push('Escola');
      erros.push('id_escola');
    }
    if (!id_projeto) {
      camposObrigatorios.push('ID Projeto');
      erros.push('id_projeto');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    // Sempre usar AUTO para gerar ID automaticamente no backend (evita conflitos de concorrência)
    const dataToSend = {
      id_pavimento: 'AUTO',
      id_escola,
      id_projeto,
      descr_pavimento: descr_pavimento === '' ? null : descr_pavimento,
      pe_direito: pe_direito === '' ? null : parseFloat(pe_direito),
      area: area === '' ? null : parseFloat(area),
      perimetro: perimetro === '' ? null : parseFloat(perimetro)
    };

    console.log('📤 Dados sendo enviados para /pavimento:', dataToSend);

    try {
      setLoading(true);
      const response = await api.post('/pavimento', dataToSend);
      console.log('✅ Resposta do servidor:', response.data);
      const idCriado = response.data.data?.id_pavimento || 'novo registro';
      setFormData({ id_pavimento: '', id_escola: '', id_projeto: '', descr_pavimento: '', pe_direito: '', area: '', perimetro: '' });
      setEscolaSearch('');
      setProjetoSearch('');
      setCamposComErro([]);
      mostrarMensagem('success', `Pavimento ${idCriado} adicionado com sucesso!`);
      carregarDados(false);
      fetchNextId();
    } catch (error) {
      console.error('❌ Erro ao adicionar pavimento:', {
        mensagem: error.message,
        status: error.response?.status,
        dadosEnviados: dataToSend,
        erroBackend: error.response?.data,
        erroCompleto: error
      });

      // Mensagem de erro mais detalhada
      let mensagemErro = 'Erro ao adicionar registro';

      if (error.response?.status === 400) {
        const backendError = error.response?.data?.message || error.response?.data?.error;
        mensagemErro = `Erro 400 - Dados inválidos: ${backendError || 'Verifique os campos numéricos (Pé Direito, Área, Perímetro)'}`;
      } else if (error.response?.status === 404) {
        mensagemErro = `Erro 404 - Escola ou Projeto não encontrado. Verifique se os IDs selecionados existem.`;
      } else if (error.response?.status === 409) {
        mensagemErro = `Erro 409 - Conflito ao gerar ID. Tente novamente.`;
      } else if (error.response?.data?.message) {
        mensagemErro = `Erro: ${error.response.data.message}`;
      }

      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'pavimento.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Pavimentos</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_pavimento</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/pavimento.mov" buttonText="Ver Tutorial Pavimento" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_pavimento">ID Pavimento: <span style={{ color: '#666', fontSize: '0.85em' }}>(automático)</span></label>
            <input
              type="text"
              id="id_pavimento"
              name="id_pavimento"
              value=""
              readOnly
              placeholder="Gerado ao adicionar"
              maxLength={255}
              disabled={true}
              style={{ backgroundColor: '#e9e9e9', cursor: 'not-allowed', color: '#888' }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="id_escola">Escola:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_escola"
              name="id_escola"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                const escolaEncontrada = escolas.find(esc =>
                  esc.nome_escola === e.target.value
                );
                if (escolaEncontrada) {
                  setFormData(prev => ({ ...prev, id_escola: escolaEncontrada.id_escola }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_escola'));
                } else {
                  setFormData(prev => ({ ...prev, id_escola: '' }));
                }
              }}
              list="escolas-list-pavimento"
              placeholder="Digite para buscar escola..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_escola') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="escolas-list-pavimento">
              {escolasFiltradas.map(item => (
                <option key={item.id_escola} value={item.nome_escola} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="id_projeto">ID Projeto:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_projeto"
              name="id_projeto"
              value={projetoSearch}
              onChange={(e) => {
                setProjetoSearch(e.target.value);
                const projetoEncontrado = projetos.find(proj =>
                  proj.id_projeto === e.target.value ||
                  `${proj.id_projeto} - ${proj.nome_escola || ''}`.trim() === e.target.value
                );
                if (projetoEncontrado) {
                  setFormData(prev => ({ ...prev, id_projeto: projetoEncontrado.id_projeto }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_projeto'));
                } else {
                  setFormData(prev => ({ ...prev, id_projeto: '' }));
                }
              }}
              list="projetos-list-pavimento"
              placeholder="Digite para buscar projeto..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_projeto') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="projetos-list-pavimento">
              {projetosFiltrados.map(item => (
                <option key={item.id_projeto} value={item.id_projeto}>
                  {item.nome_escola ? `${item.nome_escola} - ${item.descr_tp_projeto || ''}` : item.id_projeto}
                </option>
              ))}
            </datalist>
          </div>
          <div className="form-group full-width">
            <label htmlFor="descr_pavimento">Descrição:</label>
            <input type="text" id="descr_pavimento" name="descr_pavimento" value={formData.descr_pavimento} onChange={handleInputChange} placeholder="Ex: Bloco A" maxLength={100} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="pe_direito">Pé direito:</label>
            <input type="number" id="pe_direito" name="pe_direito" value={formData.pe_direito} onChange={handleInputChange} placeholder="Ex: 3,5" step="0.01" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="area">Área:</label>
            <input type="number" id="area" name="area" value={formData.area} onChange={handleInputChange} placeholder="Ex: 150,75" step="0.01" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="perimetro">Perímetro:</label>
            <input type="number" id="perimetro" name="perimetro" value={formData.perimetro} onChange={handleInputChange} placeholder="Ex: 50,25" step="0.01" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default PavimentoManager;
